// GA4 analytics helper with consent gating
export const GA_MEASUREMENT_ID = "G-846T7WR86Y";
const CONSENT_KEY = "oli_analytics_consent";
const PREFS_KEY = "oli_cookie_prefs";

export type CookieCategory = "essential" | "analytics" | "marketing" | "preferences";
export type CookiePreferences = Record<Exclude<CookieCategory, "essential">, boolean> & { essential: true };

export function getCookiePreferences(): CookiePreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      essential: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      preferences: !!parsed.preferences,
    };
  } catch {
    return null;
  }
}

export function setCookiePreferences(prefs: Omit<CookiePreferences, "essential">) {
  const full: CookiePreferences = { essential: true, ...prefs };
  localStorage.setItem(PREFS_KEY, JSON.stringify(full));
  // Keep legacy analytics consent in sync
  setConsent(prefs.analytics ? "granted" : "denied");
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: prefs.analytics ? "granted" : "denied",
      ad_storage: prefs.marketing ? "granted" : "denied",
      personalization_storage: prefs.preferences ? "granted" : "denied",
    });
  }
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export function getConsent(): "granted" | "denied" | null {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(CONSENT_KEY);
  return v === "granted" || v === "denied" ? v : null;
}

export function setConsent(value: "granted" | "denied") {
  localStorage.setItem(CONSENT_KEY, value);
  console.log(`[GA Test Mode] Consent set to: ${value}`);
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: value,
      ad_storage: "denied",
    });
    console.log(`[GA Test Mode] gtag consent updated. analytics_storage=${value}`);
    if (value === "granted") {
      loadGtagScript();
      fireTestPing();
      // Verify script load + GA initialization
      setTimeout(() => {
        const scriptPresent = !!document.querySelector(
          `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`
        );
        const dataLayerLen = window.dataLayer?.length ?? 0;
        console.log(
          `[GA Test Mode] GA initialized check → script loaded: ${scriptPresent}, dataLayer entries: ${dataLayerLen}, measurement ID: ${GA_MEASUREMENT_ID}`
        );
        if (scriptPresent && dataLayerLen > 0) {
          console.log("[GA Test Mode] ✅ Google Analytics is active and sending events.");
        } else {
          console.warn("[GA Test Mode] ⚠️ GA may not be fully initialized yet.");
        }
      }, 2000);
    } else {
      console.log("[GA Test Mode] ❌ Analytics disabled — no events will be sent.");
    }
  } else {
    console.warn("[GA Test Mode] window.gtag is not available — analytics not initialized.");
  }
}

// Fires once per browser session after consent is granted, for GA Realtime verification.
function fireTestPing() {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem("oli_ga_test_ping")) return;
    sessionStorage.setItem("oli_ga_test_ping", "1");
    // Defer to allow the gtag script to load
    setTimeout(() => {
      window.gtag?.("event", "ga_test_ping", {
        source: "init",
        ts: Date.now(),
      });
    }, 1500);
  } catch {
    // ignore
  }
}

let scriptLoaded = false;
function loadGtagScript() {
  if (scriptLoaded || typeof document === "undefined") return;
  scriptLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
}

export function initAnalytics() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // Default consent denied until user accepts
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    wait_for_update: 500,
  });
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  if (getConsent() === "granted") {
    window.gtag("consent", "update", { analytics_storage: "granted" });
    loadGtagScript();
    fireTestPing();
  }
}

export function trackPageview(path: string) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name: string, params: Record<string, any> = {}) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}
